import { Formik } from 'formik';
import {
  get,
} from 'lodash';
import makeStyles from '@mui/styles/makeStyles';
import { useMutation, useLazyQuery } from '@apollo/client';
import Avatar from '@mui/material/Avatar';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ChoosePhotoIcon from '@mui/icons-material/FolderOpen';
import PropTypes from 'prop-types';
import React, {
  Fragment,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Yup from 'yup';

import avatarSource from 'assets/avatar.jpg';
import CancelButton from 'components/buttons/Cancel';
import EditButton from 'components/buttons/Edit';
import SaveButton from 'components/buttons/Save';
import CardActions from 'components/Card/Actions';
import Button from '@mui/material/Button';
import Image from 'components/Image';
import LoadBlur from 'components/LoadBlur';
import RowSpacer from 'components/RowSpacer';
import SelectField from 'components/SelectField';
// import tracker, { TrackerFragments } from 'lib/tracker';
import apiGateway from '../../../lib/apiGateway';
import { UPDATE_USER_PROFILE, UPDATE_USER_PROFILE_HEADSHOT } from '../../../schema/apolloMutations/profileMutations';
import { getBlob, uploadUGCMiddleWare, validateFileMime } from '../../../utils/helper';
import { GET_PROFILE_INFO } from '../../../schema/apolloQueries/commonQueries';
import { GET_PERSON_INFO } from '../../../schema/apolloQueries/profileQueries';

const useStyles = makeStyles((theme) => ({
  avatar: {
    borderRadius: '50%',
    overflow: 'hidden',
    height: 80,
    width: 80,
    marginTop: 10,
    marginRight: 10,
  },
  avatarVerified: {
    borderRadius: '50%',
    overflow: 'hidden',
    height: 80,
    width: 80,
    marginTop: 10,
    marginRight: 10,
    border: 2,
    borderStyle: 'solid',
  },
  button: {
    position: 'relative',
  },
  error: {
    textAlign: 'center',
  },
  headshotContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  input: {
    opacity: 0,
    position: 'absolute',
    top: 0,
    left: 0,
  },
  list: {
    padding: 0,
    color: theme.palette.text.primary,
  },
  listItem: {
    paddingTop: 10,
    paddingBottom: 10,
    color: theme.palette.text.primary,
  },
  notification: {
    textAlign: 'center',
  },
}));
const validationSchema = Yup.object()
  .shape({
    displayName: Yup.string()
      .trim()
      .required('Display name is required'),
  });

const ProfileSecuritySettings = ({ viewer, verified }) => {
  const styles = useStyles();
  const { displayName, first, last, headshot } = viewer?.personInfo;
  const displayNameOptions = useMemo(() => ({
    first,
    last,
    first_last: `${first}_${last}`,
    last_first: `${last}_${first}`,
  }), [first, last]);
  const fileInputRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [notificationMessage, setNotificationMessage] = useState(null);
  const [fileLenghtError, setFileLengthError] = useState(null);
  const [updateUserProfile] = useMutation(UPDATE_USER_PROFILE, {
    refetchQueries: [
      GET_PERSON_INFO,
    ],
  });
  const [UpdateUserProfileHeadshot] = useMutation(UPDATE_USER_PROFILE_HEADSHOT, {
    refetchQueries: [
      GET_PERSON_INFO,
    ],
  });
  const initialValues = useMemo(
    () => ({
      displayName,
      headshot: headshot || avatarSource,
    }),
    [
      displayName,
      headshot,
    ],
  );
  // const trackerData = useMemo(
  //   () => ({ viewer }),
  //   [viewer],
  // );
  const [getProfileInfo] = useLazyQuery(GET_PROFILE_INFO);
  const handleCancel = () => {
    setEditing(false); setFileLengthError(null);
  };

  const handleEdit = useCallback(
    () => setEditing(true),
    [setEditing],
  );
  const handleFileChange = useCallback(
    () => {
      setFileLengthError(null);
      const reader = new FileReader();
      const inputFile = fileInputRef.current.files.item(0);
      if (!validateFileMime(inputFile)) {
        setFileLengthError('Please choose a valid Png, Jpeg or Heic file as profile picture.');
        return;
      }

      const name = inputFile?.name?.split('.')[0];
      if (name.length >= 64) {
        setFileLengthError('Your profile Pic. file name has exceeds 64 characters limit , Please change it and reupload.');
        return;
      }
      reader.onloadend = () => {
        setFile(inputFile);
        setImagePreviewUrl(reader.result);
      };
      reader.readAsDataURL(inputFile);
    },
    [fileInputRef],
  );

  const handleFormSubmit = (data, { setErrors, setSubmitting, setFieldValue }) => {
    const { personId } = viewer;
    setSubmitting(true);
    updateUserProfile({
      variables: {
        personId,
        person: {
          firstName: '',
          displayName: data.displayName,
        },
      },
    }).then(res => {
      if (res.data.updateUserProfile === 200) {
        setFieldValue('displayName', data.displayName);
        if (!imagePreviewUrl) {
          setEditing(false);
          setSubmitting(false);
        }
      }
    }).catch(e => {
      // eslint-disable-next-line no-console
      console.log('e', e);
    });

    if (imagePreviewUrl) {
      const uri = file.name.substring(file.name.lastIndexOf('/') + 1);
      const body = {};
      const reqData = Object.create(null);
      reqData[uri] = file.type;
      body.files = reqData;
      apiGateway.getPreSignedURLApi(body).then(async res => {
        const metaData = await getBlob(imagePreviewUrl);
        await uploadUGCMiddleWare(res[uri], metaData).then(resImage => {
          if (resImage === 200) {
            UpdateUserProfileHeadshot({
              variables: {
                fileName: uri,
                isAvatar: false,
                mimeType: file.type,
                personId,
              },
            });
            setFieldValue('headshot', imagePreviewUrl);
          }
          setNotificationMessage('Saved successfully.');
          setErrors(null);
          setError(null);
          setEditing(false);
          setSubmitting(false);
          getProfileInfo();
        }).finally(() => {
          setSubmitting(false);
        });
      });
    }

  };

  const renderForm = useCallback(
    ({
      dirty,
      errors,
      handleBlur,
      isSubmitting,
      isValid,
      setFieldTouched,
      setFieldValue,
      submitForm,
      resetForm,
      values,
    }) => {
      const canSave = (dirty || file) && isValid && !isSubmitting;
      return (
        <LoadBlur loading={isSubmitting}>
          {editing
            ? (
              <Fragment>
                {error && (
                  <div className={styles.error}>
                    {error}
                  </div>
                )}

                <List className={styles.list}>
                  <ListItem
                    className={styles.listItem}
                  >
                    <div>
                      <ListItemText
                        primary='Headshot'
                      />
                      <div
                        className={styles.headshotContainer}
                      >
                        <Avatar
                          className={verified ? styles.avatarVerified : styles.avatar}
                        >
                          <Image
                            alt='User headshot'
                            showLoading={false}
                            src={imagePreviewUrl || get(values, 'headshot')}
                          />
                        </Avatar>
                        <Button
                          component='label'
                          role={undefined}
                          // variant='contained'
                          tabIndex={-1}
                          startIcon={<ChoosePhotoIcon />}
                        >
                          {!file
                            ? 'Choose photo'
                            : 'Change photo'}
                          <input
                            accept='image/png,image/jpg,image/jpeg'
                            type='file'
                            onChange={handleFileChange}
                            ref={fileInputRef}
                            className={styles.input}
                          />
                        </Button>
                      </div>
                      {fileLenghtError && <div style={{ color: 'red' }}>{fileLenghtError}</div>}
                    </div>
                  </ListItem>
                  <ListItem
                    className={styles.listItem}
                  >
                    <SelectField
                      disabled={isSubmitting}
                      error={get(errors, 'displayName')}
                      label='Display Name'
                      fullWidth
                      name='displayName'
                      onBlur={e => {
                        e.target.name = 'displayName';
                        handleBlur(e);
                      }}
                      onChange={(event, key, data) => {
                        setFieldTouched('displayName', true);
                        setFieldValue('displayName', data);
                      }}
                      showEmpty={!get(values, 'displayName')}
                      options={displayNameOptions}
                      value={get(values, 'displayName')}
                    />
                  </ListItem>
                </List>
              </Fragment>
            )
            : (
              <Fragment>
                {notificationMessage && (
                  <div className={styles.notification}>
                    {notificationMessage}
                  </div>
                )}
                <List className={styles.list}>
                  <ListItem
                    className={styles.listItem}
                  >
                    <div>
                      <ListItemText
                        primary='Headshot'
                      />
                      <Avatar
                        className={verified ? styles.avatarVerified : styles.avatar}
                      >
                        <Image
                          alt='User headshot'
                          showLoading={false}
                          src={values.headshot}
                        />
                      </Avatar>
                    </div>
                  </ListItem>
                  <ListItem
                    className={styles.listItem}
                  >
                    <ListItemText
                      primary='Display Name'
                      secondary={displayNameOptions[values.displayName]}
                    />
                  </ListItem>
                </List>
              </Fragment>
            )}
          <CardActions>
            {editing && (
              <CancelButton
                disabled={isSubmitting}
                onClick={() => {
                  resetForm();
                  handleCancel();
                }}
                secondary
              />
            )}
            <RowSpacer />
            {editing && (
              <SaveButton
                disabled={!canSave}
                onClick={submitForm}
                primary={canSave}
              />
            )}
            {!editing && (
              <EditButton
                forceLabel
                onClick={handleEdit}
                primary
              />
            )}
          </CardActions>
        </LoadBlur>
      );
    },
    [file, headshot, editing, error, styles.error, styles.list, styles.listItem, styles.headshotContainer, styles.avatarVerified, styles.avatar, styles.button, styles.input, styles.notification, verified, imagePreviewUrl, handleFileChange, fileLenghtError, displayNameOptions, notificationMessage, handleCancel, handleEdit],
  );
  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      isInitialValid={validationSchema.isValidSync(initialValues)}
      onSubmit={handleFormSubmit}
      validationSchema={validationSchema}
    >
      {renderForm}
    </Formik>
  );
};

ProfileSecuritySettings.propTypes = {
  viewer: PropTypes.object.isRequired,
  verified: PropTypes.bool,
};

export default ProfileSecuritySettings;
